import getCookie, { COOKIE_MH_TOKEN } from '../cookies';
import environment, { CheckoutFlow, isLocalEnv } from '../environment';
import { Product } from '../../features/cart/shop-cart/interfaces/shop-cart.interface';
import { Sort } from '../../features/search/interfaces/search.interface';
import { ItemType } from '../../features/product-card/product.interface';
import { PaymentOrderItemData } from '../../features/order-confirmation/order-completion.interface';
import { ProductCardData } from '../../features/product-card/product-card-display';
import { OfferTypeChecker } from './offer-type-util';
import { fetchAleksProductTitleVerbose, isAleksSharpenCompanionBundle } from './aleks-sharpen-bundle-util';

export const UNIFIED = 'UNIFIED';
export const HE = 'HE';
export const HIGHERED = 'HIGHERED';
export const SEG = 'SEG';
export const SEG_PROGRAM = 'SEG_PROGRAM';
export const SEG_STORE = 'SEG_STORE';
export const PROFESSIONAL = 'PROFESSIONAL';
export const CONTENT = 'CONTENT';
export const SEG_PRODUCT = 'SEG_PRODUCT';
export const CONNECT_LOOSELEAF_UPSELL = 'Connect Looseleaf Upsell';
export const CONNECT = 'connect';

export const PRODUCTS = 'PRODUCTS';
export const ARTICLES = 'ARTICLES';

export const ALL_LOWER = 'all';
export const UNIFIED_LOWER = 'unified';
export const HE_LOWER = 'he';
export const SEG_LOWER = 'seg';
export const SEG_STORE_LOWER = 'seg-store';
export const PROFESSIONAL_LOWER = 'professional';
export const CONTENT_LOWER = 'content';
export const SEG_PRODUCT_LOWER = 'seg-product';
export const PRODUCTS_LOWER = 'products';
export const ARTICLES_LOWER = 'articles';

const ids: Array<string> = [];
const catalogMapping: { [key: string]: string } = {
  [ALL_LOWER]: UNIFIED,
  [UNIFIED_LOWER]: UNIFIED,
  [HE_LOWER]: HE,
  [SEG_LOWER]: SEG_PROGRAM,
  [SEG_STORE_LOWER]: SEG_STORE,
  [PROFESSIONAL_LOWER]: PROFESSIONAL,
  [CONTENT_LOWER]: CONTENT,
  [SEG_PRODUCT_LOWER]: SEG_PRODUCT,
  [PRODUCTS_LOWER]: PRODUCTS,
  [ARTICLES_LOWER]: ARTICLES,
};

const reverseCatalogMapping: { [key: string]: string } = {
  [UNIFIED]: UNIFIED_LOWER,
  [HE]: HE_LOWER,
  [SEG_PROGRAM]: SEG_LOWER,
  [SEG]: SEG_LOWER,
  [SEG_STORE]: SEG_STORE_LOWER,
  [PROFESSIONAL]: PROFESSIONAL_LOWER,
  [CONTENT]: CONTENT_LOWER,
  [SEG_PRODUCT]: SEG_PRODUCT_LOWER,
  [PRODUCTS]: PRODUCTS_LOWER,
  [ARTICLES]: ARTICLES_LOWER,
};

const oneYear = 365 * 24 * 60 * 60 * 1000;

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function getUniqueId(): string {
  const id: string = (Date.now() * Math.random()).toString();

  if (ids.includes(id)) {
    return getUniqueId();
  } else {
    ids.push(id);
    return id;
  }
}

export function replace(str: string, placeholder: string, val: string | undefined) {
  return val !== undefined && val.length > 0 ? str.replace(placeholder, val) : str;
}

export function createMessage(template: string, values: { [key: string]: string | undefined }, ariaLive?: string) {
  const parts = template.split('%');
  const message = document.createElement('SPAN');
  if (ariaLive) {
    message.setAttribute('aria-live', ariaLive);
  }
  let inPlaceholder = false;
  let missingPlaceholder = false;

  parts.forEach((part) => {
    if (inPlaceholder) {
      const value = values[part];
      if (value !== undefined) {
        const span = document.createElement('SPAN');
        span.setAttribute('part', part);
        span.innerText = value;
        message.appendChild(span);
      } else {
        missingPlaceholder = true;
      }
    } else {
      const text = document.createTextNode(part);
      message.appendChild(text);
    }
    inPlaceholder = !inPlaceholder;
  });

  return missingPlaceholder ? document.createElement('SPAN') : message;
}

function isAllLower(text) {
  return /^[a-z-]+$/.test(text);
}

export function mapCatalog(catalog: string) {
  if (isAllLower(catalog)) {
    return catalogMapping[catalog];
  }
  return catalog;
}

export function reverseCatalogMap(catalog: string) {
  // Case-insensitive lookup: the mapping is keyed by UPPERCASE catalog constants
  // (e.g. 'HE', 'PRODUCTS'), but URL params arrive lowercase (?bu=he). Try the raw key
  // first for backward-compat, then fall back to the uppercase form.
  return reverseCatalogMapping[catalog] ?? reverseCatalogMapping[String(catalog ?? '').toUpperCase()];
}

/**
 * Maps catalog values to user-friendly labels matching the search catalog selector
 * @param catalog The catalog value to map (e.g., 'PRODUCTS', 'SEG_PROGRAM', etc.)
 * @returns The user-friendly label (e.g., 'View All', 'PreK-12', etc.)
 */
export function getCatalogLabel(catalog: string): string {
  const catalogLabels: { [key: string]: string } = {
    UNIFIED: 'All',
    PRODUCTS: 'All',
    SEG_PROGRAM: 'PreK-12',
    HE: 'Higher Education',
    CONTENT: 'Topics & Ideas',
    ARTICLES: 'Articles',
    PROFESSIONAL: 'Professional',
  };
  return catalogLabels[catalog] || catalog;
}

export function getEditionSuffix(edition: number, isEvergreen: boolean): string {
  if (edition > 0 && !isEvergreen) {
    return ', ' + ordinalSuffixOf(edition) + ' Edition';
  } else {
    return '';
  }
}

export function productTitle(product: Product): string {
  if (product.productMetadata.isEvergreen) {
    return product.productMetadata.title;
  } else if (
    isTunnelPurchaseFlow() &&
    isAleksSharpenCompanionBundle(product.productMetadata?.offerType, product.productIdentifier?.isSharpenCompanion)
  ) {
    return `${fetchAleksProductTitleVerbose(product.productMetadata?.metaOfferContentsMetadata)}`;
  } else {
    return (
      product.productMetadata.title +
      getEditionSuffix(product.productMetadata.edition, product.productMetadata.isEvergreen)
    );
  }
}

export function isHigheredEbook(product: PaymentOrderItemData | ProductCardData): boolean {
  return product?.businessUnit === HE && OfferTypeChecker.isHeEbook(product?.offerType);
}

export function getProductTitleForPdpLevelAnalytics(
  title?: string,
  edition?: number,
  isEvergreenProduct = false,
  isSharpenPlus = false
) {
  if (isEvergreenProduct || isSharpenPlus) {
    return title;
  } else {
    return title + getEditionSuffix(edition!, isEvergreenProduct);
  }
}

export function ordinalSuffixOf(i: string | number) {
  if (typeof i === 'string') {
    try {
      i = Number(i);
    } catch (error) {
      return i;
    }
  }
  const j = i % 10,
    k = i % 100;
  if (j == 1 && k != 11) {
    return i + 'st';
  }
  if (j == 2 && k != 12) {
    return i + 'nd';
  }
  if (j == 3 && k != 13) {
    return i + 'rd';
  }
  return i + 'th';
}

/**
 * @param date - Can be of date or string type
 * @returns A string formatted following the format: Jul 13th, 2022
 */

export function formatDateDefault(date: Date | string) {
  if (typeof date === 'string') {
    date = new Date(date);
  }

  const year = date.getFullYear();
  const monthName = months[date.getMonth()];
  const monthDate = date.getDate();

  return `${monthName} ${ordinalSuffixOf(monthDate)}, ${year}`;
}

/**
 * @param array The array to turn into a string
 * @param joiner The string to use to join all but the last element of the array. Defaults to ', '.
 * @param ending The string to use the last element of the array. Defaults to ' and '.
 * @returns A string where all but the last element of the array is
 *          joined with "joiner" and the last element is joined with "ending".
 *          If the array is null or the length is 0, an empty string is returned.
 *          If the array of length 2, the "joiner" is ignored and the two elements
 *          are joined with "ending".
 *
 * Example: formattedJoin(['1', '2', '3']) ==> '1, 2, and 3'
 * Example: formattedJoin(['1', '2']) ==> '1 and 2'
 * Example: formattedJoin(['1']) ==> '1'
 * Example: formattedJoin([]) ==> ''
 */
export function formattedJoin(array: Array<string>, joiner = ', ', ending = ' and ') {
  if (!array) {
    return '';
  } else if (array.length <= 2) {
    return array.join(ending);
  } else {
    return array.slice(0, -1).join(joiner) + ending + array[array.length - 1];
  }
}

/**
 * Formats author values.
 *
 * It also removes any occurrence of 'N/A' from the authors string and cleans up empty values.
 *
 * @param authorsJoined - authors to be formatted
 * @param delimiter - delimiter that will be used to split the authors
 * @param joiner - token used to separate the authors except for the last
 * @param ending - token used to separate the last author
 * @returns
 */
export function formattedAuthors(authorsJoined: string, delimiter = ',', joiner = ', ', ending = ' and ') {
  if (!authorsJoined) {
    return authorsJoined;
  }

  const authors = stripNotApplicableValues(authorsJoined)
    .split(delimiter)
    .filter((author) => author.trim());

  return formattedJoin(authors, joiner, ending);
}

/**
 * Removes any occurrence of 'N/A' from the input string.
 *
 * @param input
 */
export function stripNotApplicableValues(input: string): string {
  return input.replace(/\s*N\/A\s*/g, '');
}

export function repeatThis(count: number) {
  return {
    times: (callback: Function) => Array.from(Array(count)).map(() => callback()),
  };
}

/**
 * @param ms The number of milliseconds to wait before resolving.
 * @returns a promise that resolves in the given number of milliseconds.
 */
export async function wait(ms: number) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(true), ms);
  });
}

export function numberSuffix(edition: number): string {
  if (['11', '12', '13'].includes(edition.toString())) {
    return 'th';
  }
  switch (edition.toString().substr(edition.toString().length - 1, 1)) {
    case '1':
      return 'st';
    case '2':
      return 'nd';
    case '3':
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * @param event An event object containing an 'a' tag in the event target ancestry. If any other object is provided, undefined will be returned.
 * @returns The href of either the event target or of the closest 'a' tag in the targets ancestry.
 */
export function findEventHref(event: any): string | undefined {
  return event?.target.nodeName !== 'image' && event?.target?.href
    ? event.target.href
    : event?.target?.closest('a')?.href;
}

/**
 * @param href Any string. Anything that is not a valid href will cause the function to return false.
 * @returns True if and only if the origin of the given href does not match the current window location's origin.
 */
export function isExternalUrl(href: string) {
  return !!safelyGetUrl(href) && safelyGetUrl(href)?.origin !== safelyGetUrl(window.location.href)?.origin;
}

/**
 * Constructs the browser standard URL object: https://developer.mozilla.org/en-US/docs/Web/API/URL.
 *
 * @param href The href to construct a URL object with.
 * @returns The URL object representing the given href.
 */
export function safelyGetUrl(href: string) {
  try {
    return new URL(href);
  } catch {
    return undefined;
  }
}

/**
 * This method passes through shadow roots and so can be used from deeply nested web components.
 *
 * @param elem The element to start looking from.
 * @param query The query to use to find a matching element.
 * @returns The closest parent element matching the query.
 */
export function findClosest(elem: any, query: string) {
  const found = elem?.closest(query);
  const host = elem?.getRootNode()?.host;
  if (found) {
    return found;
  } else if (host) {
    return findClosest(host, query);
  } else {
    return undefined;
  }
}

/**
 * Replaces an input string in snake case to an uppercase title.
 *
 * For example, 'this_is_a_test' becomes 'This Is A Test'.
 *
 * @param input
 */
export function snakeCaseToTitle(input: string): string {
  return capitalizeText(input.replace(/_/g, ' '));
}

export function capitalizeText(text: string) {
  return text
    .split(/ +/)
    .map((text) => text[0].toUpperCase() + text.slice(1).toLowerCase())
    .join(' ');
}

/**
 * This method is to check if provided text contains HTML Tags
 *
 * @param text - Should be of type string
 */
export function isTextContainsHTMLTags(text: string) {
  const htmlTagRegex = /<\/?[a-z][\s\S]*>/i;
  return htmlTagRegex.test(text);
}

/**
 * This method is used to trim HTML Tags and &nbsp; in provided string
 *
 * @param htmlString - Should be of type string
 */
export function stripHTMLTags(htmlString: string): string {
  if (!htmlString) {
    return '';
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  let text = doc.body.textContent ?? '';
  // Some values (e.g. a fragment that begins with head-only elements such as
  // <title>/<style>, or otherwise re-nested markup) are parsed outside <body>,
  // leaving it empty. Fall back to fragment parsing via <template>, which uses a
  // neutral context and preserves the readable text regardless of tag structure.
  if (!text.trim()) {
    const template = document.createElement('template');
    template.innerHTML = htmlString;
    template.content.querySelectorAll('script, style').forEach((el) => el.remove());
    text = template.content.textContent ?? '';
  }
  return text.replace(/\u00A0/g, ' ');
}

export function truncatingCleanString(string: string, size: number = 294) {
  const strippedText = isTextContainsHTMLTags(string) ? stripHTMLTags(string) : string;
  return strippedText?.length > size ? strippedText.substring(0, size) + '... ' : strippedText;
}

/**
 * Escapes special regex characters in a string so it can be safely used
 * as a literal pattern inside `new RegExp()`.
 *
 * @param value - The string to escape.
 */
export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export async function retryApiCall(fnToCall: Function, onFail: Function | null, maxRetry = 2) {
  for (let i = 0; i < maxRetry; i++) {
    try {
      return await fnToCall();
    } catch (error) {
      if (onFail) {
        await onFail();
      }
    }
  }
}

export function addAuthTokenToCookieForLocalDevelopment() {
  const doesCookieContainAuthToken = !!document.cookie?.split('; ').find((value) => value.includes('MH_TOKEN='));

  if (doesCookieContainAuthToken) return;

  const query = new URL(location.href);
  const token = query.searchParams.get(COOKIE_MH_TOKEN);
  if (token) {
    console.log('called', document.cookie);
    const expiryTime = new Date(new Date().getTime() + oneYear).toString();
    document.cookie = `MH_TOKEN=${token}; path=/; expires=${expiryTime}`;
  }
}

export function isTunnelPurchaseFlow() {
  return environment().checkoutFlow === CheckoutFlow.tunnelPurchase;
}

export function isStandaloneAleksTunnelPurchase(
  itemType?: string,
  offerType?: string,
  isSharpenCompanion?: boolean,
  application?: string
): boolean {
  return (
    (OfferTypeChecker.isAleks(offerType) || itemType === ItemType.aleks) &&
    !isSharpenCompanion &&
    application === ItemType.aleks
  );
}

export function isAleksLooseleaf(tunnelProduct: PaymentOrderItemData | ProductCardData | undefined) {
  return tunnelProduct?.application === ItemType.aleksll;
}

export function isNationalFlow() {
  return environment().checkoutFlow === CheckoutFlow.national;
}

export function isHeStorePurchaseFlow() {
  return environment().checkoutFlow === CheckoutFlow.hestores;
}

export function isK12StorePurchaseFlow() {
  return environment().checkoutFlow === CheckoutFlow.k12stores;
}

export function addAuthTokenToCookieForSession(token) {
  const mhToken = `MH_TOKEN=${token};domain=.mheducation.com;path=/;expires=session;${isLocalEnv() ? '' : 'secure'}`;
  document.cookie = mhToken;
}

export function addAuthTokenToCookie(token) {
  const expiryTime = parseMhJwtToken().session_timeout * 1000;
  const expiryIn = new Date(new Date().getTime() + expiryTime).toString();
  document.cookie = `MH_TOKEN=${token};path=/;expires=${expiryIn};secure`;
}

function parseMhJwtToken() {
  var cookie = getCookie(COOKIE_MH_TOKEN).split('.')[1];
  var base64 = cookie.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (val) {
        return '%' + ('00' + val.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
  return JSON.parse(jsonPayload);
}

export const SortByValueMapping: { [key: string]: Array<Sort> | [] } = {
  relevance: [],
  title_asc: [{ name: 'TITLE', direction: 'ASCENDING' }],
  title_desc: [{ name: 'TITLE', direction: 'DESCENDING' }],
  copyright_asc: [{ name: 'COPYRIGHT_YEAR', direction: 'ASCENDING' }],
  copyright_desc: [{ name: 'COPYRIGHT_YEAR', direction: 'DESCENDING' }],
  plp_copyright_asc: [
    { name: 'COPYRIGHT_YEAR', direction: 'ASCENDING' },
    { name: 'TITLE', direction: 'ASCENDING' },
  ],
  plp_copyright_desc: [
    { name: 'COPYRIGHT_YEAR', direction: 'DESCENDING' },
    { name: 'TITLE', direction: 'ASCENDING' },
  ],
  price_asc: [
    { name: 'PRICE', direction: 'ASCENDING' },
    { name: 'TITLE', direction: 'ASCENDING' },
  ],
  price_desc: [
    { name: 'PRICE', direction: 'DESCENDING' },
    { name: 'TITLE', direction: 'ASCENDING' },
  ],
};

/**
 * This function is return the value associated to the given search parameter
 * @param name
 * @returns value of search param
 */
export function getQueryParam(name) {
  const urlParams = new URLSearchParams(location.search);
  if (urlParams.get(name)) {
    return urlParams.get(name);
  }
  return '';
}

export function isSearchPage() {
  const pathname = location.pathname.toLowerCase();
  return /(^|\/)search(\.html)?$/.test(pathname);
}

export function cleanIsbn(isbn?: string): string | undefined {
  return isbn?.replaceAll('-', '');
}

export function isCheckoutPage() {
  if (isLocalEnv()) {
    return location.pathname.includes('/checkout');
  }
  return location.pathname.startsWith('/checkout');
}

export function isDesktop(): boolean {
  const userAgent: string = navigator.userAgent || navigator.vendor;

  const isMobileDevice = (): boolean => {
    const regexs = [/(Android)(.+)(Mobile)/i, /BlackBerry/i, /iPhone|iPod/i, /Opera Mini/i, /IEMobile/i];
    return regexs.some((b) => userAgent.match(b));
  };

  const isTabletDevice = (): boolean => {
    const regex =
      /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/;
    return regex.test(userAgent.toLowerCase());
  };
  const isDesktop = !isMobileDevice() && !isTabletDevice();
  return isDesktop;
}

/**
 * In this function the scrollTop property sets the content is scrolled vertically to top
 */
export function scrollTopInDesktop() {
  if (isDesktop()) {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }
}

/**
 * @param asyncFn
 * @returns
 *
 * it cancels the previous request to solve the race condition
 */
export const abortWrapper = (asyncFn) => {
  let controller: AbortController | null = null;

  return async (...args) => {
    if (controller) {
      controller.abort(); // Abort previous call if it exists
    }

    controller = new AbortController(); // Create a new abort controller
    const signal = controller.signal;

    try {
      const result = await asyncFn(...args, signal); // Call the original async function with the signal
      controller = null; // Reset controller after successful completion
      return result;
    } catch (error: any) {
      console.error('error => ', error);
      if (error.name === 'AbortError') {
        // Handle the abort error if needed
        console.log('Previous call aborted');
      } else {
        // Handle other errors
        throw error;
      }
    }
  };
};

/**
 * Compare two strings ignoring the case sensitive.
 * If any value other than a valid string is passed,
 * the result will be false.
 */
export function equalsIgnoreCase(string1: String, string2: String) {
  if (!string1 || !string2) return false;
  return string1.toLocaleLowerCase() === string2.toLocaleLowerCase();
}

/**
 * @param text - Should be of type string
 * Copies the text to clipboard
 */
export function copyTextToClipboard(text: string) {
  navigator.clipboard?.writeText(text);
}

/**
 * @param jwt - Should be a valid jwt token
 * Decodes the payload of a valid jwt token without validating any signature
 */
export function decodeJWTPayload(jwt: string) {
  const base64Url = jwt.split('.')[1];
  // JWT uses base64url (RFC 4648 §5), so using only atob (which uses base64) isn't enough.
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
  return JSON.parse(jsonPayload);
}

/**
 * Safely parses a JSON string, returning undefined if the string is not valid JSON.
 *
 * @param value
 */
export function parse(value: any): any {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}
